import { useContext, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import {
  DataGrid,
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams
} from '@mui/x-data-grid';
import { Theme, Tooltip, useTheme } from '@mui/material';
import {
  BaseJobMetadata,
  CompletedExport,
  CompletedJobMetadata,
  FailedExport,
  FailedJobMetadata,
  PendingExport
} from '@esg/esg-global-types';
import DownloadingIcon from '@mui/icons-material/Downloading';
import { MetadataError } from '@ep/error-handling';
import { Query, QueryDocumentSnapshot, collection, onSnapshot, query } from 'firebase/firestore';
import moment from 'moment';
import { ReactComponent as ExportSuccessIcon } from '../../../assets/icons/Correct.svg';
import { ReactComponent as ExportFailedIcon } from '../../../assets/icons/Incorrect.svg';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { UserContext } from '../../../context/UserContext';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { db } from '../../../lib/google/firebase';
import { ReactComponent as DownloadIcon } from '../../../assets/icons/Download.svg';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import DataGridToolbar from '../../shared/datagrid/DataGridToolbar';
import { COMPONENT_UNKNOWN_FAILURE, FEEDBACK_ACTION_FAILURE } from '../../../util/global_variables';
import { export_label } from '../../../lib/metric_capture/export';

const SOURCE = 'ExportDataGrid.tsx';

type GridRowExportMetadata = BaseJobMetadata | FailedJobMetadata | CompletedJobMetadata;

export interface ExportDataGridRow {
  id: string;
  metadata: GridRowExportMetadata;
  message: string;
  status: 'Pending' | 'Completed' | 'Failed';
}

interface ExportDataGridProps {
  parent_rows?: Array<ExportDataGridRow>;
  handleExportNotification: (latestRow: ExportDataGridRow | null) => void;
  handleExportDownload: (row: ExportDataGridRow) => void;
  handleHideExportNotification: () => void;
}

/**
 * A datagrid widget for displaying all pending, failed and completed generated data exports for company.
 * @param {Array<ExportDataGridRow>} parent_rows Optionally provide data exports from parent, auto fetches all exports from firestore if not provided.
 * @param {boolean | undefined} grid_loading Optionally set data grid to loading state from parent.
 * @returns {JSX.Element}
 */
const ExportDataGrid = ({
  parent_rows,
  handleExportNotification,
  handleExportDownload,
  handleHideExportNotification
}: ExportDataGridProps) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const { user } = useContext(UserContext);

  if (!group || !company || !user) return null;

  const [pendingDataExports, setPendingDataExports] = useState<Array<ExportDataGridRow> | null>(
    null
  );
  const [failedDataExports, setFailedDataExports] = useState<Array<ExportDataGridRow> | null>(null);
  const [completedDataExports, setCompletedDataExports] = useState<Array<ExportDataGridRow> | null>(
    null
  );
  const [gridLoading, setGridLoading] = useState<boolean>(true);
  const [exportCompleted, setExportCompleted] = useState<boolean>(false);
  const previousLatestRowId = useRef<string | null | undefined>(undefined);
  const theme: Theme = useTheme();

  // Combine all types of exports into grid rows.
  const data_export_rows: Array<ExportDataGridRow> | null =
    pendingDataExports && failedDataExports && completedDataExports
      ? [...pendingDataExports, ...completedDataExports, ...failedDataExports]
      : null;

  // Filter export rows based on current user and role.
  const getAllowedExportRows = (total_rows: Array<ExportDataGridRow>): Array<ExportDataGridRow> => {
    return total_rows.filter(
      (row: ExportDataGridRow) => (row.metadata.requested_by?.id ?? '') === user.id
    );
  };

  // Only show rows that current user is allowed to see.
  const allowed_rows: Array<ExportDataGridRow> = getAllowedExportRows(
    parent_rows ?? (data_export_rows ? data_export_rows : [])
  );

  const completed_allowed_rows: Array<ExportDataGridRow> = allowed_rows.filter(
    (row) => 'completed' in row.metadata
  );

  const getLatestCompletedRow = (rows: Array<ExportDataGridRow>): ExportDataGridRow | null => {
    return (rows || []).reduce(
      (previous, row) => {
        if (!previous) return row;
        if ('completed' in previous.metadata && 'completed' in row.metadata) {
          return previous.metadata.completed > row.metadata.completed ? previous : row;
        } else return previous;
      },
      null as ExportDataGridRow | null
    );
  };

  const latest_row = getLatestCompletedRow(completed_allowed_rows);

  const latest_row_id: string | null = latest_row?.id ?? '';

  // Get all pending, completed and failed exports for current company and create collection listeners if rows not provided by parent, attach full export type object.
  useEffect(() => {
    setGridLoading(true);
    try {
      if (group && company && user && parent_rows === undefined) {
        const collection_prefix = `groups/${group.id}/companies/${company.id}`;
        const completed_exports_query: Query = query(
          collection(db, `${collection_prefix}/completed_exports`)
        );
        const failed_exports_query: Query = query(
          collection(db, `${collection_prefix}/failed_exports`)
        );
        const pending_exports_query: Query = query(
          collection(db, `${collection_prefix}/pending_exports`)
        );
        onSnapshot(completed_exports_query, (querySnapshot) => {
          const completed_exports: Array<CompletedExport> = [];
          querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
            if (doc.data().metadata) {
              completed_exports.push({
                id: doc.id,
                metadata: doc.data().metadata,
                message: doc.data().message
              });
            }
          });
          setCompletedDataExports(
            completed_exports.map((completed_export: CompletedExport) => {
              return {
                id: completed_export.id,
                metadata: completed_export.metadata,
                message: completed_export.message,
                status: 'Completed'
              };
            })
          );
        });
        onSnapshot(failed_exports_query, (querySnapshot) => {
          const failed_exports: Array<FailedExport> = [];
          querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
            if (doc.data().metadata) {
              failed_exports.push({
                id: doc.id,
                metadata: doc.data().metadata,
                message: doc.data().message
              });
            }
          });
          setFailedDataExports(
            failed_exports.map((failed_export: FailedExport) => {
              return {
                id: failed_export.id,
                metadata: failed_export.metadata,
                message: failed_export.message,
                status: 'Failed'
              };
            })
          );
        });
        onSnapshot(pending_exports_query, (querySnapshot) => {
          const pending_exports: Array<PendingExport> = [];
          querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
            if (doc.data().metadata) {
              pending_exports.push({
                id: doc.id,
                metadata: doc.data().metadata,
                message: doc.data().message
              });
            }
          });
          setPendingDataExports(
            pending_exports.map((pending_export: PendingExport) => {
              return {
                id: pending_export.id,
                metadata: pending_export.metadata,
                message: pending_export.message,
                status: 'Pending'
              };
            })
          );
        });
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : COMPONENT_UNKNOWN_FAILURE(SOURCE),
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to fetch exports', tracking_id),
        state: true,
        type: 'error'
      });
    } finally {
      setGridLoading(false);
    }
  }, [group, company]);

  useEffect(() => {
    // Use previous row to buffer the useEffect for the first load
    if (previousLatestRowId.current === undefined) {
      previousLatestRowId.current = latest_row_id;
      return;
    }
    // If nothing has changed between this useEffect and the previous, return
    if (latest_row_id === previousLatestRowId.current) return;

    previousLatestRowId.current = latest_row_id;
    if (!latest_row) return;
    handleExportNotification(latest_row);
    setExportCompleted(true);
  }, [latest_row_id, latest_row]);

  // Clean-up that clears the highlighted row and export notification on group and company change
  useEffect(() => {
    previousLatestRowId.current = undefined;
    setExportCompleted(false);
    handleHideExportNotification();
  }, [group, company]);

  const columns: Array<GridColDef> = [
    {
      field: 'status',
      maxWidth: 40,
      headerName: '',
      renderCell: (params: GridRenderCellParams) => {
        return (
          (params.row.status === 'Pending' && (
            <>
              <Tooltip title="Started" key={1}>
                <DownloadingIcon color="info" />
              </Tooltip>
            </>
          )) ||
          (params.row.status === 'Failed' && (
            <>
              <Tooltip title="Failed" key={2}>
                <ExportFailedIcon
                  height={30}
                  stroke={theme.palette.error.main}
                  fill="none"
                  strokeWidth={5}
                />
              </Tooltip>
            </>
          )) ||
          (params.row.status === 'Completed' && (
            <>
              <Tooltip title="Completed" key={3}>
                <ExportSuccessIcon
                  height={30}
                  stroke={theme.palette.success.main}
                  fill="none"
                  strokeWidth={5}
                />
              </Tooltip>
            </>
          ))
        );
      },
      flex: 0.5
    },
    {
      field: 'export_type',
      headerName: 'Export Type',
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.metadata.key
          ? typeof params.row.metadata.key === 'string'
            ? params.row.metadata.key.replaceAll('_', ' ')
            : params.row.metadata.key
          : '';
      },
      flex: 1
    },
    {
      field: 'started',
      headerName: 'Started',
      valueGetter: (params: GridValueGetterParams) =>
        moment(params.row.metadata.started.toDate()).format('YYYY-MM-DD HH:mm'),
      flex: 1
    },
    {
      field: 'finished',
      headerName: 'Finished',
      valueGetter: (params: GridValueGetterParams) =>
        params.row.metadata.completed
          ? moment(params.row.metadata.completed.toDate()).format('YYYY-MM-DD HH:mm')
          : params.row.metadata.failed
            ? moment(params.row.metadata.failed.toDate()).format('YYYY-MM-DD HH:mm')
            : '',
      flex: 1
    },
    {
      field: 'message',
      headerName: 'Message',
      sortable: false,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value}>
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {params.value}
          </span>
        </Tooltip>
      ),
      flex: 3
    },
    {
      field: 'actions',
      type: 'actions',
      align: 'center',
      flex: 1,
      getActions: ({ row }) => {
        const export_actions: Array<React.ReactElement<GridActionsCellItemProps>> = [];
        if (row.status === 'Completed') {
          export_actions.push(
            <>
              <Tooltip title="Download Export" key={4}>
                <GridActionsCellItem
                  icon={
                    <span style={{ color: theme.palette.primary.main, display: 'contents' }}>
                      <DownloadIcon width={25} />
                    </span>
                  }
                  id="download-export"
                  label="Download"
                  onClick={async () => {
                    try {
                      setGridLoading(true);
                      await handleExportDownload(row);
                    } catch (err: unknown) {
                      const tracking_id: string = generateUuidV4();
                      log(
                        'error',
                        new MetadataError(
                          err instanceof Error ? err.message : COMPONENT_UNKNOWN_FAILURE(SOURCE),
                          null,
                          tracking_id
                        )
                      );
                      setFeedbackData({
                        message: FEEDBACK_ACTION_FAILURE('Unable to download export', tracking_id),
                        state: true,
                        type: 'error'
                      });
                    } finally {
                      setGridLoading(false);
                    }
                  }}
                />
              </Tooltip>
            </>
          );
        }
        return export_actions;
      }
    }
  ];

  return (
    <Box>
      <DataGrid
        loading={gridLoading}
        autoHeight
        rows={allowed_rows}
        columns={columns}
        disableColumnMenu
        getRowClassName={(params) =>
          params.row.id === latest_row_id && 'completed' in params.row.metadata && exportCompleted
            ? 'Mui-selected'
            : ''
        }
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 5
            }
          },
          sorting: {
            sortModel: [{ field: 'started', sort: 'desc' }]
          },
          columns: {
            columnVisibilityModel: {
              completed: false
            }
          }
        }}
        pageSizeOptions={[5]}
        disableRowSelectionOnClick
        slots={{
          toolbar: () => <DataGridToolbar entity_label={export_label} />,
          noRowsOverlay: () => <DataGridNoRows missing_label={export_label} />
        }}
        sx={{
          '&, [class^=MuiDataGrid]': { border: 'none' }
        }}
      />
    </Box>
  );
};

export default ExportDataGrid;
